import { useAppliances } from '@hakimo-ui/hakimo/data-access';
import { AppliancesFilters } from '@hakimo-ui/hakimo/types';
import { QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import {
  useDebounce,
  useRowNavigation,
  useUrlSyncedFilters,
  withAuthz,
} from '@hakimo-ui/hakimo/util';
import { AdminListHeader } from '@hakimo-ui/shared/ui-base';
import { useEffect, useMemo, useState } from 'react';
import AppliancesFilterPanel from './AppliancesFilterPanel';
import AppliancesListCard from './AppliancesListCard';
import {
  defaultUrlState,
  getSearchParams,
  parseUrlState,
  serializeUrlState,
  ViewMode,
} from './searchParams';
import { defaultFilters, hasFiltersApplied } from './util';

function AppliancesListBase() {
  const [urlState, setUrlState] = useUrlSyncedFilters(
    defaultUrlState,
    serializeUrlState,
    parseUrlState
  );
  const { search, filters, page, viewMode } = urlState;
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [searchInput, setSearchInput] = useState(search);

  const debouncedSearch = useDebounce(searchInput, 400);
  const onClickRow = useRowNavigation('/appliances');

  useEffect(() => {
    if (debouncedSearch !== search) {
      setUrlState((prev) => ({ ...prev, search: debouncedSearch, page: 1 }));
    }
  }, [debouncedSearch, search, setUrlState]);

  const searchParams = useMemo(
    () => getSearchParams(search, filters, page),
    [search, filters, page]
  );
  const appliancesQuery = useAppliances(searchParams);
  const { data } = appliancesQuery;

  const setViewMode = (value: ViewMode) =>
    setUrlState((prev) => ({ ...prev, viewMode: value }));
  const setPage = (value: number) =>
    setUrlState((prev) => ({ ...prev, page: value }));

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchInput(e.target.value);
  const onApplyFilters = (f: AppliancesFilters) => {
    setUrlState((prev) => ({ ...prev, filters: f, page: 1 }));
    setOpenFilterPanel(false);
  };
  const clearAll = () => {
    setSearchInput('');
    setUrlState((prev) => ({
      ...prev,
      search: '',
      filters: defaultFilters,
      page: 1,
    }));
  };

  return (
    <Page title="Appliances" titleFontSize="large">
      <div className="flex h-full flex-col px-4 py-4">
        <AdminListHeader
          searchValue={searchInput}
          onSearchChange={onSearchChange}
          hasFiltersApplied={hasFiltersApplied(filters)}
          onClickFilter={() => setOpenFilterPanel(true)}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <QueryResult queryResult={appliancesQuery}>
            {(response) => (
              <AppliancesListCard
                response={response}
                viewMode={viewMode}
                page={page}
                loading={appliancesQuery.isLoading}
                onClickRow={onClickRow}
                onClear={clearAll}
                onChangePage={setPage}
              />
            )}
          </QueryResult>
        </div>
      </div>
      <AppliancesFilterPanel
        open={openFilterPanel}
        filters={filters}
        facets={data?.facets}
        tenantNames={data?.tenantNames}
        onClose={() => setOpenFilterPanel(false)}
        onApplyFilters={onApplyFilters}
      />
    </Page>
  );
}

export const AppliancesList = withAuthz(AppliancesListBase, [
  'appliances:view',
]);

export default AppliancesList;
